import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  ArrowRight,
  Menu,
  X,
  Search,
  ShieldCheck,
  Car,
  TrendingDown,
  Wrench,
  Scale,
  Calculator,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import SearchModal from "./SearchModal";
import { useQuoteModal } from "../lib/quoteModal";

// Iter-172 — Mobile drawer final polish. The iter-171 vertical nav list
// is now grouped into two named sections (COMPARE + TOOLS & LEARN) for
// stronger IA hierarchy. Icons bumped to w-8 h-8 (+14% over iter-171's
// w-7), descriptions sharpened, 200ms interaction animations added.
// Tone references: NerdWallet / Mercury / Ramp / Robinhood.
const navGroups = [
  {
    label: "Compare & Save",
    id: "compare",
    items: [
      {
        to: "/auto-insurance",
        label: "Auto Insurance",
        id: "insurance",
        description: "Compare rates from trusted providers.",
        Icon: ShieldCheck,
      },
      {
        to: "/auto-loans",
        label: "Auto Loans",
        id: "loans",
        description: "Find financing with competitive APRs.",
        Icon: Car,
      },
      {
        // Iter-202 (homepage/footer audit follow-up, user approved) —
        // Refinance is the highest-AOV vertical hub but was previously
        // nav-invisible (footer-only). Now a first-class nav item on
        // desktop + the mobile drawer.
        to: "/refinance",
        label: "Refinance",
        id: "refinance",
        description: "Lower your existing car payment.",
        Icon: TrendingDown,
      },
      {
        to: "/warranty",
        label: "Warranties",
        id: "warranty",
        description: "Compare vehicle protection plans.",
        Icon: Wrench,
      },
      {
        to: "/compare",
        label: "Compare",
        id: "compare",
        description: "Side-by-side provider comparisons.",
        Icon: Scale,
      },
    ],
  },
  {
    label: "Tools & Learn",
    id: "tools-learn",
    items: [
      {
        to: "/calculators",
        label: "Calculators",
        id: "calculators",
        description: "Estimate savings in seconds.",
        Icon: Calculator,
      },
      {
        to: "/guides",
        label: "Guides",
        id: "guides",
        description: "Learn how to save on car ownership.",
        Icon: BookOpen,
      },
    ],
  },
];

// Flattened view kept for the desktop nav (still a simple horizontal
// strip) and the iter-154 branded-icon-coverage regression gate.
// Desktop bar omits "Compare" (redundant with the Compare Offers CTA, owner
// decision); the mobile drawer still lists it for internal linking.
const navItems = navGroups.flatMap((g) => g.items).filter((n) => n.to !== "/compare");

// Iter-185.T — The drawer "Most Popular" strip was removed per user
// audit feedback ("de-duplicate drawer / inconsistent row patterns").
// Compare Insurance + Compare Auto Loans + Use Calculators all duplicated
// items already present in the structured Compare & Save / Tools & Learn
// sections, and the strip's label-only rows were the only nav rows
// without descriptions — visual inconsistency. The full catalog remains
// reachable via the two grouped sections; SearchModal still surfaces
// the "Popular searches" mirror for Cmd/Ctrl+K power users.

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { open: openQuoteModal } = useQuoteModal();

  // Close mobile drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // Track scroll to add subtle shadow + backdrop blur once user scrolls past hero
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Iter-417 — The header "Compare" CTA is ALWAYS visible, on every page
  // and at every scroll position. The iter-275.AW/415 hero-visibility gate
  // (scrollY flag → IntersectionObserver on the hero form) was the root
  // cause of the mobile "button appears, disappears after scrolling, then
  // reappears" defect: the observer's ratio band toggled the slot to
  // `opacity-0 invisible` while the hero form was on screen. One source
  // of truth now: no section-based rule exists.

  // Cmd/Ctrl+K opens search globally
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll while drawer is open + flag so CSS can suppress
  // platform-injected badges (e.g. `#emergent-badge`) that would otherwise
  // overlap the Start Comparing CTA on small screens.
  // Iter-418 — Scroll lock moved from <body> to <html>. `body {
  // overflow: hidden }` turns the body into its own (non-scrolling)
  // scrollport, which makes the sticky header resolve against the top
  // of the DOCUMENT instead of the viewport — so opening the drawer
  // mid-page teleported the header (and the drawer with it) thousands
  // of px above the fold. Locking the documentElement preserves the
  // scroll position and keeps `position: sticky` intact.
  useEffect(() => {
    if (open) {
      const prev = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
      document.body.classList.add("mobile-menu-open");
      return () => {
        document.documentElement.style.overflow = prev;
        document.body.classList.remove("mobile-menu-open");
      };
    }
  }, [open]);

  return (
    <header
      data-testid="site-header"
      data-scrolled={scrolled ? "true" : "false"}
      className={`sticky top-0 z-50 w-full transition-[box-shadow,background-color,border-color] duration-200 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200"
          : "bg-white border-b border-slate-200"
      }`}
    >
      {/* Three zones: logo | nav | actions. Nav is truly centred from 1280px (xl),
          where the side columns are wide enough; below that it follows the logo. */}
      <div className="max-w-wide mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4 xl:grid xl:grid-cols-[1fr_auto_1fr]">
        <Link
          to="/"
          data-testid="logo-link"
          aria-label="CarSavr home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center shrink-0 justify-self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-4"
        >
          {/* Brand kit wordmark — outlined vector, no webfont dependency. */}
          <img
            src="/brand/carsavr-wordmark-primary.svg"
            alt="CarSavr"
            width="108"
            height="20"
            data-testid="header-wordmark"
            className="h-5 w-auto"
          />
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-4 xl:gap-7 lg:mr-auto lg:ml-8 xl:m-0">
          {navItems.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-link-${n.id}`}
              className={({ isActive }) =>
                `relative inline-flex items-center h-16 whitespace-nowrap text-[13px] xl:text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:text-slate-950 focus-visible:underline focus-visible:underline-offset-4 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:transition-colors after:duration-150 ${
                  isActive ? "text-slate-950 after:bg-[#10B981]" : "text-slate-600 hover:text-slate-950 after:bg-transparent"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 justify-self-end">
          {/* Search trigger — Iter-82: removed the ⌘K kbd badge from
              the visible pill. The shortcut still works (handled in the
              useEffect above) but power-user UX has no business making
              the consumer-facing search bar look developer-tooling. Pill
              now reads as a single editorial "Search" affordance with
              the icon left-aligned and a subtle focus ring. */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            data-testid="header-search-trigger"
            aria-label="Open site search"
            className="hidden md:inline-flex items-center justify-center gap-2 h-11 lg:h-10 w-11 xl:w-auto xl:px-3.5 border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-slate-600 hover:text-slate-950 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-2"
          >
            <Search className="w-4 h-4 shrink-0" aria-hidden />
            <span className="hidden xl:inline leading-none">Search</span>
          </button>
          {/* Iter-417 — mobile search lives inside the hamburger drawer (see below). */}

          {/* Iter-185.V — Header CTA now visible on mobile too.
              Previously `hidden sm:inline-flex` (≥640px only) cut off
              the primary conversion lever on iPhones — user feedback:
              "(Compare Now) button is hidden please change sizing and
              or words to make fit." Mobile shows compact "Compare"
              (no arrow) to fit alongside the 44px hamburger; sm:+
              shows full "Compare Offers →" treatment.

              Iter-275.AW (Phase 4 #8 — Production Readiness Audit).
              Above-fold CTA dedupe: on the homepage, hide this header
              CTA while the hero is still in viewport (`!scrolled`) so
              it doesn't duplicate the "Compare Insurance Quotes" hero
              submit button within the same viewport. Once the user
              scrolls past the hero, the header CTA snaps back in as a
              persistent sticky conversion lever. On every non-homepage
              Iter-415 — Flicker fix. The gate moved off the 8px
              `scrolled` threshold onto an IntersectionObserver watching
              the hero `quote-form-card` (see effect above). The button
              is always mounted inside a fixed-width slot and merely
              faded/`invisible` when hidden, so its appearance never
              reflows the Search button or the hamburger (zero CLS). */}
          <span data-testid="header-cta-slot" data-cta-visible="true" className="inline-flex">
            <Button
              data-testid="header-cta"
              onClick={() => openQuoteModal()}
              className="group inline-flex items-center bg-[#10B981] hover:bg-brand-emerald-press text-[#0A2540] font-bold rounded-none h-11 lg:h-10 min-w-[44px] px-3.5 sm:px-4 text-[13px] sm:text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-[#0A2540] focus-visible:ring-offset-2"
            >
              <span className="sm:hidden">Compare</span>
              <span className="hidden sm:inline leading-none">Compare Offers</span>
              <ArrowRight aria-hidden className="w-4 h-4 ml-1.5 hidden sm:inline transition-transform duration-200 group-hover:translate-x-[3px] motion-reduce:transition-none" />
            </Button>
          </span>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden w-11 h-11 grid place-items-center text-slate-950 hover:bg-slate-100 transition-colors"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — Iter-171 redesign: vertical premium list with
          "Most Popular" header, borderless emerald icons, descriptions,
          chevron affordance + slide-in-from-right animation (250ms). */}
      {/* Iter-418 — Drawer + backdrop are portalled to <body>. The
          scrolled header carries `backdrop-blur-md`, and a
          `backdrop-filter` establishes a containing block for
          `position: fixed` descendants — so the drawer was being
          positioned against the 64px-tall header box instead of the
          viewport (and the backdrop collapsed to ~1px tall). */}
      {open && createPortal(
        <>
          {/* Backdrop — fades in over the 250ms window. */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-backdrop"
            className="lg:hidden fixed inset-0 top-16 bg-slate-950/40 backdrop-blur-sm z-40 animate-[carsavrFadeIn_250ms_ease-out_forwards]"
          />
          <nav
            id="mobile-menu"
            data-testid="mobile-menu"
            // Iter-275.AA (Phase G — Safari/iOS) — `calc(100vh-4rem)` against
            // the LARGE viewport hid the bottom of the open mobile menu
            // behind the iOS Safari URL bar (the bar adds ~75px back to the
            // viewport on scroll-up but `100vh` doesn't shrink). `100dvh`
            // (dynamic viewport height, Tailwind 3.4+) auto-shrinks when the
            // URL bar is visible so the menu's max-height matches what the
            // user actually sees.
            className="lg:hidden fixed top-16 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-cardHover max-h-[calc(100dvh-4rem)] overflow-y-auto animate-[carsavrSlideInFromRight_250ms_cubic-bezier(0.16,1,0.3,1)_forwards]"
          >
            {/* Iter-417 — Search moved from the header bar into the drawer on
                mobile so the sticky header stays: logo | Compare | menu. */}
            <button
              type="button"
              onClick={() => { setOpen(false); setSearchOpen(true); }}
              data-testid="mobile-menu-search"
              className="w-full flex items-center gap-3 px-6 min-h-[52px] border-b border-slate-200 text-left text-sm font-semibold text-slate-950 hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
            >
              <Search className="w-4 h-4 text-slate-500" />
              Search CarSavr
              <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Guides · tools · savings</span>
            </button>
            {/* Iter-185.T — "Most Popular" strip removed (drawer
                de-duplication). The structured grouped lists below
                cover the same destinations with consistent row
                patterns (icon + title + description + chevron). */}

            {/* Primary nav — Iter-172 grouped vertical list (COMPARE +
                TOOLS & LEARN sections). Borderless emerald icons, title,
                short description, chevron. 200ms interaction polish on
                row hover/active: icon brightens, arrow slides right,
                row tints subtle emerald. Iter-173 — section spacing
                increased to ~16px between groups for premium breathing
                room. Iter-185.T — top padding bumped pt-5 → pt-6 since
                Most Popular no longer provides the top-edge breathing
                room. */}
            <div data-testid="mobile-menu-nav-list" className="pt-6 pb-3">
              {navGroups.map((group, gIdx) => (
                <section
                  key={group.id}
                  data-testid={`mobile-menu-group-${group.id}`}
                  className={gIdx === 0 ? "" : "mt-6 pt-5 border-t border-slate-100"}
                >
                  <p
                    data-testid={`mobile-menu-group-label-${group.id}`}
                    className="px-6 mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500"
                  >
                    {group.label}
                  </p>
                  <ul>
                    {group.items.map((n) => {
                      const Icon = n.Icon;
                      return (
                        <li key={n.id}>
                          <NavLink
                            to={n.to}
                            data-testid={`mobile-nav-link-${n.id}`}
                            className={({ isActive }) =>
                              `group flex items-center gap-4 px-6 py-3.5 transition-colors duration-200 active:bg-[#10B981]/8 ${
                                isActive
                                  ? "bg-[#FAFAFA]"
                                  : "hover:bg-[#FAFAFA]"
                              }`
                            }
                          >
                            <Icon
                              className="w-8 h-8 text-[#10B981] shrink-0 opacity-90 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200"
                              strokeWidth={1.8}
                              aria-hidden
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-bold text-[16px] leading-tight text-slate-950">
                                {n.label}
                              </div>
                              <div className="text-[13px] text-slate-500 leading-snug mt-0.5">
                                {n.description}
                              </div>
                            </div>
                            <ChevronRight
                              className="w-5 h-5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-1 group-active:translate-x-1 group-active:text-[#10B981] transition-all duration-200 shrink-0"
                              aria-hidden
                            />
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>

            {/* CTA — Iter-185.W: trust tagline removed per user audit
                ("remove this text so button is higher up"). The CTA now
                sits directly under the eyebrow with no intermediate
                copy block, lifting the button closer to the thumb-zone. */}
            <div className="px-6 pt-5 pb-7 border-t border-slate-100 bg-gradient-to-b from-white to-brand-surface-soft">
              <p
                data-testid="mobile-menu-cta-eyebrow"
                className="text-[11px] font-semibold text-slate-500 mb-2.5 text-center"
              >
                Ready to start?
              </p>
              <Button
                data-testid="mobile-menu-cta"
                onClick={() => {
                  setOpen(false);
                  openQuoteModal();
                }}
                className="w-full bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold rounded-none h-12 transition-all duration-200"
              >
                Compare Offers
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </nav>
          {/* Inline keyframes — no tailwind.config.js edit needed. The
              250ms ease-out fade + slide-in-from-right gives the drawer
              a premium fintech entrance without excessive motion. The
              Iter-173 #emergent-badge suppression keeps the platform
              badge from overlapping the Start Comparing CTA while the
              drawer is open. */}
          <style>{`
            @keyframes carsavrFadeIn {
              from { opacity: 0 }
              to { opacity: 1 }
            }
            @keyframes carsavrSlideInFromRight {
              from { opacity: 0; transform: translateX(16px); }
              to { opacity: 1; transform: translateX(0); }
            }
            body.mobile-menu-open #emergent-badge {
              opacity: 0 !important;
              pointer-events: none !important;
              visibility: hidden !important;
            }
            @media (prefers-reduced-motion: reduce) {
              [data-testid="mobile-menu"],
              [data-testid="mobile-menu-backdrop"] {
                animation: none !important;
              }
            }
          `}</style>
        </>,
        document.body,
      )}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
